/*
  The home page's words, shared by the website and the app so the two can't drift. Each client lays
  them out its own way.
*/

/** A run of text, bold where the copy leans on it. */
export type RichText = (string | { bold: string })[];

/** The two cards that introduce us to a newcomer: the deal, and how the week's difficulty runs. */
export const HOME_PITCH = {
  daily: {
    title: 'New puzzles, every day.',
    intro: 'Made to pair with your morning coffee, your daily commute, or your bedtime routine.',
    perks: [
      { title: 'Always Free', body: 'Yes, really.' },
      { title: 'Zero Ads', body: 'Nothing popping up between you and your puzzle.' },
      { title: 'No streaks attached', body: 'Miss a day? Catch up anytime this week.' },
    ],
  },
  difficulty: {
    /** One line each. */
    title: ['Gentle Mondays.', 'Tough Sundays.'],
    paragraphs: [
      [
        'Our puzzles are ',
        { bold: 'hard by design.' },
        ' Each one has a ',
        { bold: 'single solution' },
        ' you can reach with logic alone, ',
        { bold: 'no guessing.' },
      ],
      [
        "First time here? We'll ",
        { bold: 'start you on Monday.' },
        ' By the weekend, the puzzles challenge even our regulars.',
      ],
    ] as RichText[],
  },
};

/** Players' words, shown one at a time. */
export const HOME_QUOTES: { content: string; author: string }[] = [
  {
    content: '... learning Stars has made my brain feel the way my muscles feel after a workout.',
    author: '— Nancy, age 55',
  },
  {
    content:
      'I actually had poured my coffee into my cup, but then let it sit for the entirety of finishing this Fields.',
    author: '— Anonymous',
  },
  {
    content: "I just started playing Stars last night. Now I'm not so mad about NYT putting the mini behind a paywall.",
    author: '— Jared',
  },
  {
    content: "Be careful... it quickly becomes an obsession!! Best game I've ever played. So addicting! ❤️",
    author: '— Louise',
  },
];

/**
 * Every puzzle ever solved, as the "Over 16.4 million puzzles solved" counter shows it: millions to one
 * decimal place, rounded down so "over" stays true. Null below a million, where the boast falls flat.
 */
export function solvedMillions(total: number | null | undefined): string | null {
  if (total == null || total < 1_000_000) return null;
  return (Math.floor(total / 100_000) / 10).toFixed(1);
}

const SOLVED_FLAPS = 30;
const SOLVED_FLAP_MS = 2000;

/**
 * The counter's digits flip into place like a station's split-flap board when it comes into view: the
 * whole figure counts up from zero in `flips` steps, one every `flipMs`, each tile turning only when its
 * digit changes.
 */
export const SOLVED_FLAP = { flips: SOLVED_FLAPS, durationMs: SOLVED_FLAP_MS, flipMs: SOLVED_FLAP_MS / SOLVED_FLAPS };

/**
 * The faces each digit tile of `millions` shows, first to last, skipping the point: the figure counting
 * up from all zeros to `millions` in even steps. Tile `i` is showing `faces[i][min(step, flips)]` after
 * `step` flips.
 */
export function solvedFlapFaces(millions: string): string[][] {
  const digits = [...millions].filter((char) => char !== '.');
  const target = Number(digits.join(''));
  const counts = Array.from({ length: SOLVED_FLAPS + 1 }, (_, step) =>
    String(Math.floor((target * step) / SOLVED_FLAPS)).padStart(digits.length, '0')
  );
  return digits.map((_, index) => counts.map((count) => count[index]));
}
